<template>
  <v-navigation-drawer
    :value="value"
    @input="$emit('input', $event)"
    app
    width="256"
    mini-variant-width="72"
    :mini-variant="miniState"
    mobile-breakpoint="960"
    class="side-drawer"
  >
    <div
      class="side-drawer__brand"
      :class="{ 'side-drawer__brand--mini': miniState }"
    >
      <img
        v-if="!miniState"
        :src="logo"
        alt="Kairos Afrika"
        class="side-drawer__logo"
      />
      <v-btn
        icon
        small
        class="side-drawer__collapse"
        @click.stop="miniState = !miniState"
      >
        <v-icon size="20">{{
          miniState ? "mdi-chevron-right" : "mdi-chevron-left"
        }}</v-icon>
      </v-btn>
    </div>
    <p v-if="!miniState && orgName" class="side-drawer__org">{{ orgName }}</p>

    <div class="side-drawer__nav" @click.capture="expandIfMini">
      <v-list nav dense class="side-nav">
        <v-tooltip
          v-for="item in linkItem"
          :key="item.title"
          right
          :disabled="!miniState"
        >
          <template v-slot:activator="{ on }">
            <v-list-item
              :to="item.link"
              router
              class="side-nav__item"
              v-on="on"
            >
              <v-list-item-icon>
                <v-icon>{{ item.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title v-text="item.title" />
              </v-list-item-content>
            </v-list-item>
          </template>
          <span>{{ item.title }}</span>
        </v-tooltip>

        <v-list-group
          v-for="item in items"
          :key="item.title"
          :prepend-icon="item.icon"
          no-action
          class="side-nav__group"
        >
          <template v-slot:activator>
            <v-list-item-content>
              <v-list-item-title v-text="item.title" />
            </v-list-item-content>
          </template>

          <v-list-item
            v-for="subItem in item.items"
            :key="subItem.title"
            :to="subItem.link"
            router
            class="side-nav__item side-nav__item--sub"
          >
            <v-list-item-content>
              <v-list-item-title v-text="subItem.title" />
            </v-list-item-content>
          </v-list-item>
        </v-list-group>
      </v-list>
    </div>
  </v-navigation-drawer>
</template>

<script>
import logo from "@/assets/brand/kairos-afrika-logo.png";

export default {
  name: "Drawer",
  props: {
    // v-model: drawer visibility (open/closed). Owned by AppTemplate.
    value: {
      type: Boolean,
      default: null
    },
    // .sync: icon-rail (mini) state. Owned by AppTemplate so the header
    // hamburger can toggle it too.
    mini: {
      type: Boolean,
      default: false
    },
    profile: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      logo,
      linkItem: [
        {
          icon: "mdi-view-dashboard-outline",
          title: "Dashboard",
          link: { name: "admin.home.dashboard" }
        }
      ],
      items: [
        {
          icon: "mdi-account-group-outline",
          title: "Staff",
          items: [
            { title: "All Staff", link: { name: "admin.staffs.all" } }
          ]
        },
        {
          icon: "mdi-cash-multiple",
          title: "Transactions",
          items: [
            { title: "Transactions", link: { name: "admin.transactions.all" } },
            { title: "Exports", link: { name: "admin.transactions.exports" } }
          ]
        },
        {
          icon: "mdi-chart-box-outline",
          title: "Analytics",
          items: [
            {
              title: "Statistics",
              link: { name: "admin.transactions.statistics" }
            },
            {
              title: "MSISDN Statistics",
              link: { name: "admin.transactions.msisdn-statistics" }
            }
          ]
        },
        {
          icon: "mdi-delete-outline",
          title: "Trash",
          items: [{ title: "Staff", link: { name: "admin.trash.staff" } }]
        }
      ]
    };
  },
  computed: {
    miniState: {
      get() {
        return this.mini;
      },
      set(val) {
        this.$emit("update:mini", val);
      }
    },
    orgName() {
      return this.profile && this.profile.organizationName
        ? this.profile.organizationName
        : "";
    }
  },
  methods: {
    // In rail mode a collapsed group can't show its children, so a click on a
    // group header expands the drawer first; direct links still navigate.
    expandIfMini(e) {
      if (this.miniState && e.target.closest(".v-list-group__header")) {
        this.miniState = false;
      }
    }
  }
};
</script>

<style scoped>
.side-drawer {
  background: var(--sidebar) !important;
  border-right: 1px solid var(--sidebar-border) !important;
}
.side-drawer__brand {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 16px 6px;
  min-height: 52px;
}
.side-drawer__brand--mini {
  justify-content: center;
  padding: 18px 8px 6px;
}
.side-drawer__logo {
  height: 26px;
  width: auto;
  max-width: 150px;
}
.side-drawer__collapse {
  color: var(--muted-foreground) !important;
}
.side-drawer__org {
  margin: 0 16px 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--muted-foreground);
}
.side-drawer__nav {
  padding: 6px 8px;
}

/* nav items */
.side-nav >>> .v-list-item {
  border-radius: var(--radius);
  min-height: 44px;
  color: var(--sidebar-foreground);
  margin-bottom: 2px;
}
.side-nav >>> .v-list-item .v-icon {
  color: var(--sidebar-foreground);
}
.side-nav >>> .v-list-item__title {
  font-size: 14px;
  font-weight: 500;
}
.side-nav >>> .v-list-item--active {
  background: var(--signal-soft);
  color: var(--signal);
}
.side-nav >>> .v-list-item--active .v-icon,
.side-nav >>> .v-list-item--active .v-list-item__title {
  color: var(--signal);
}
.side-nav >>> .v-list-item--active::before {
  opacity: 0;
}
.side-nav__item--sub >>> .v-list-item__title,
.side-nav >>> .side-nav__item--sub .v-list-item__title {
  font-weight: 500;
}

/* rail (mini) mode: center icons, hide group chevrons */
.side-drawer.v-navigation-drawer--mini-variant >>> .v-list-item__icon {
  margin-left: 0 !important;
  margin-right: 0 !important;
}
.side-drawer.v-navigation-drawer--mini-variant
  >>> .v-list-group__header__append-icon {
  display: none;
}
</style>
